import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Search, Upload, ChevronRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { trpc } from '@/lib/trpc';
import type { SkillAgent } from '../../../../shared/types/index';

interface AttachedSkill {
  name: string;
  prompt: string;
}

interface SkillPickerPopoverProps {
  value: AttachedSkill | null;
  onChange: (skill: AttachedSkill | null) => void;
  disabled?: boolean;
}

function extractPromptFromMd(raw: string): { name: string; prompt: string } {
  const frontmatterMatch = raw.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (frontmatterMatch) {
    const fm = frontmatterMatch[1];
    const body = frontmatterMatch[2].trim();
    const nameMatch = fm.match(/winclap-name:\s*"?([^"\n]+)"?/);
    return { name: nameMatch?.[1]?.trim() ?? 'Skill adjunto', prompt: body };
  }
  return { name: 'Skill adjunto', prompt: raw.trim() };
}

export function SkillPickerPopover({ value, onChange, disabled }: SkillPickerPopoverProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'marketplace' | 'file'>('marketplace');
  const [search, setSearch] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data } = trpc.skills.getAll.useQuery(undefined, { enabled: open && tab === 'marketplace' });
  const skills = Array.isArray(data) ? data : [];

  const filtered = skills.filter((s: SkillAgent) => {
    const q = search.toLowerCase();
    return !q || s.name.toLowerCase().includes(q) || s.description?.toLowerCase().includes(q);
  });

  function selectSkill(s: SkillAgent) {
    const currentVersion = s.versions?.find((v) => v.isCurrent) ?? s.versions?.[s.versions.length - 1];
    if (!currentVersion?.prompt) return;
    onChange({ name: s.name, prompt: currentVersion.prompt });
    setOpen(false);
  }

  async function handleFile(file: File) {
    if (!file.name.match(/\.(skill|md|txt)$/i)) return;
    const raw = await file.text();
    const parsed = extractPromptFromMd(raw);
    if (!parsed.prompt) return;
    onChange({ name: file.name.replace(/\.(skill|md|txt)$/i, ''), prompt: parsed.prompt });
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          disabled={disabled}
          title={value ? t('skills.picker.skill_active_title', { name: value.name }) : t('skills.picker.attach_skill_title')}
          className={`flex items-center justify-center w-8 h-8 rounded-lg border transition-colors disabled:opacity-40 ${
            value
              ? 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/20'
              : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        >
          <Sparkles className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-80 p-0 shadow-lg">
        {/* Tabs */}
        <div className="flex border-b border-border">
          {(['marketplace', 'file'] as const).map((tabKey) => (
            <button
              key={tabKey}
              onClick={() => setTab(tabKey)}
              className={`flex-1 py-2 text-xs font-medium transition-colors ${
                tab === tabKey
                  ? 'text-primary border-b-2 border-primary bg-primary/5'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tabKey === 'marketplace' ? t('skills.picker.tab_marketplace') : t('skills.picker.tab_file')}
            </button>
          ))}
        </div>

        {tab === 'marketplace' && (
          <div className="flex flex-col">
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('skills.picker.search_placeholder')}
                  className="w-full pl-7 pr-2 py-1.5 text-xs rounded-md border border-border bg-muted/30 focus:outline-hidden focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto">
              {filtered.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-6">{t('skills.picker.no_results')}</p>
              )}
              {filtered.map((s: SkillAgent) => (
                <button
                  key={s.id}
                  onClick={() => selectSkill(s)}
                  className="w-full flex items-start gap-2 px-3 py-2 hover:bg-muted/50 transition-colors text-left group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{s.name}</p>
                    {s.description && (
                      <p className="text-[11px] text-muted-foreground truncate mt-0.5">{s.description}</p>
                    )}
                    {s.team && (
                      <span className="inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {s.team}
                      </span>
                    )}
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === 'file' && (
          <div className="p-3">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={async (e) => {
                e.preventDefault();
                setDragOver(false);
                const file = e.dataTransfer.files[0];
                if (file) await handleFile(file);
              }}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center gap-2 h-28 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'
              }`}
            >
              <Upload className="w-6 h-6 text-muted-foreground" />
              <p className="text-xs text-muted-foreground text-center">
                {t('skills.picker.upload_prompt')}<br />
                <span className="text-[11px]">{t('skills.picker.upload_hint')}</span>
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".skill,.md,.txt"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) await handleFile(file);
              }}
            />
          </div>
        )}

        {value && (
          <div className="border-t border-border p-2">
            <button
              onClick={() => { onChange(null); setOpen(false); }}
              className="w-full text-xs text-destructive hover:text-destructive/80 transition-colors py-1"
            >
              {t('skills.picker.remove_active_skill')}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
